/**
 * Navigation progress bar.
 *
 * The bar only appears when a navigation is actually slow: start()
 * arms a show timer, and a navigation that completes within
 * PROGRESS_SHOW_DELAY_MS never paints the bar at all. Prefetched and
 * cached pages swap in tens of milliseconds; flashing a progress bar
 * over those made every fast transition feel broken.
 *
 * Once shown, it is a two-stage indeterminate bar: it jumps to 30%,
 * holds briefly, then animates to 70% while the request is in
 * flight, and finally to 100% on completion. The hold between 30%
 * and 70% is intentional UX pacing - users need to see the initial
 * stage before the bar moves further. complete() waits for the final
 * width transition via `transitionend` rather than hardcoding a
 * cleanup delay. A start() while the bar is still visible (rapid
 * successive navigations) reuses it without blinking it off.
 *
 * The widget owns its DOM: it creates both elements in the
 * constructor, mounts them into document.body, and keeps private
 * references. No module-level mutable globals, no shared state.
 */

const PROGRESS_SHOW_DELAY_MS = 150;
const PROGRESS_HOLD_MS = 150;

export class ProgressBar {
  private readonly bar: HTMLDivElement;
  private readonly fill: HTMLDivElement;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private visible = false;

  constructor() {
    this.bar = document.createElement('div');
    this.bar.className = 'sdx-navigate-bar';
    this.fill = document.createElement('div');
    this.fill.className = 'sdx-navigate-bar__fill';
    this.bar.appendChild(this.fill);
    document.body.appendChild(this.bar);
  }

  start(): void {
    this.clearTimers();
    // The class, not `visible`, is the on-screen truth: a completed bar
    // stays painted until its transitionend, and a navigation started in
    // that window must reuse it in place instead of blinking it off and
    // re-arming the delay.
    if (this.bar.classList.contains('is-active')) {
      this.show();
      return;
    }
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      this.show();
    }, PROGRESS_SHOW_DELAY_MS);
  }

  complete(): void {
    if (this.showTimer !== null) {
      // Fast navigation: the bar never appeared, keep it that way.
      this.clearTimers();
      return;
    }
    if (!this.visible) {
      return;
    }
    this.clearTimers();
    this.visible = false;
    this.fill.style.width = '100%';
    const cleanup = (): void => {
      // A navigation that started after this completion owns the bar
      // again; leave its state alone.
      if (this.visible) {
        return;
      }
      this.bar.classList.remove('is-active');
      this.fill.style.width = '0';
    };
    this.fill.addEventListener('transitionend', cleanup, { once: true });
  }

  private show(): void {
    this.visible = true;
    this.bar.classList.add('is-active');
    this.fill.style.width = '30%';
    this.holdTimer = setTimeout(() => {
      this.holdTimer = null;
      this.fill.style.width = '70%';
    }, PROGRESS_HOLD_MS);
  }

  private clearTimers(): void {
    if (this.showTimer !== null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    if (this.holdTimer !== null) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
  }
}
